import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Download, Trash2, Loader2, FileText } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';

export const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp';
const MAX_BYTES = 10 * 1024 * 1024;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atff_token')}` });

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const uploadDocument = async ({ file, documentType, orderId }) => {
  if (file.size > MAX_BYTES) throw new Error('File is larger than 10 MB');
  const form = new FormData();
  form.append('file', file);
  form.append('document_type', documentType);
  if (orderId) form.append('order_id', orderId);
  try {
    await axios.post(`${API}/documents/upload`, form, { headers: authHeaders() });
  } catch (err) {
    throw new Error(err.response?.data?.detail || 'Upload failed');
  }
};

export const downloadUploadedDocument = async (doc) => {
  const res = await axios.get(`${API}/documents/download/${doc.document_id}`, {
    headers: authHeaders(),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.original_filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const DocumentRow = ({ doc, currentUserId, onDownload, onDelete, busy }) => (
  <div className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-white" data-testid={`uploaded-doc-${doc.document_id}`}>
    <div className="flex items-center gap-3 min-w-0">
      <FileText className="w-5 h-5 text-continental shrink-0" />
      <div className="min-w-0">
        <p className="font-medium text-sm truncate" data-testid="uploaded-doc-name">{doc.original_filename}</p>
        <p className="text-xs text-gray-500">
          {!['order_document', 'business_document'].includes(doc.document_type) && (
            <span className="font-medium text-continental capitalize" data-testid="uploaded-doc-type">{doc.document_type?.replace(/_/g, ' ')} · </span>
          )}
          {formatSize(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString()}
          {doc.uploader_name && ` · ${doc.uploader_name}`}
        </p>
      </div>
    </div>
    <div className="flex gap-2 shrink-0">
      <Button size="sm" variant="outline" onClick={() => onDownload(doc)} disabled={busy === doc.document_id} data-testid={`download-doc-${doc.document_id}`}>
        {busy === doc.document_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </Button>
      {doc.user_id === currentUserId && (
        <Button size="sm" variant="outline" onClick={() => onDelete(doc)} data-testid={`delete-doc-${doc.document_id}`}>
          <Trash2 className="w-4 h-4 text-red-600" />
        </Button>
      )}
    </div>
  </div>
);

export const UploadedDocuments = ({ orderId, currentUserId, title = 'My Uploaded Files', refreshKey = 0, onChange }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);
  const inputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const url = orderId ? `${API}/documents/order/${orderId}` : `${API}/documents/my`;
      const res = await axios.get(url, { headers: authHeaders() });
      setDocs(res.data.documents || []);
    } catch {
      toast.error('Could not load files');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs, refreshKey]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadDocument({ file, documentType: orderId ? 'order_document' : 'business_document', orderId });
      toast.success(`${file.name} uploaded`);
      fetchDocs();
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    setBusy(doc.document_id);
    try {
      await downloadUploadedDocument(doc);
    } catch {
      toast.error('Download failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (doc) => {
    try {
      await axios.delete(`${API}/documents/${doc.document_id}`, { headers: authHeaders() });
      setDocs((prev) => prev.filter((d) => d.document_id !== doc.document_id));
      toast.success('File removed');
      onChange?.();
    } catch {
      toast.error('Could not remove file');
    }
  };

  return (
    <div className="space-y-3" data-testid={orderId ? 'order-uploaded-documents' : 'my-uploaded-documents'}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-charcoal">{title}</p>
          <p className="text-xs text-gray-500">PDF, Word, Excel or images, up to 10 MB</p>
        </div>
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleFile} data-testid="document-file-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="bg-continental hover:bg-continental/90" data-testid="upload-document-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {uploading ? 'Uploading...' : 'Upload File'}
        </Button>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      ) : docs.length === 0 ? (
        <p className="text-sm text-gray-500 p-4 border border-dashed rounded-lg text-center" data-testid="no-uploaded-docs">No files uploaded yet</p>
      ) : (
        docs.map((doc) => (
          <DocumentRow key={doc.document_id} doc={doc} currentUserId={currentUserId} onDownload={handleDownload} onDelete={handleDelete} busy={busy} />
        ))
      )}
    </div>
  );
};

export default UploadedDocuments;
